import React from 'react';
import { Box, Container, Typography, Paper, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          marginBottom: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/StableTrack.png"
          alt="StableTrack"
          sx={{
            height: 100,
            width: 100,
            mb: 2,
            cursor: 'pointer',
          }}
          onClick={() => navigate('/')}
        />

        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 1 }}>
            Privacy Policy
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Effective Date: January 1, 2025 | Version 1.0
          </Typography>

          <Typography variant="body1" paragraph>
            At StableTrack, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our veterinary practice management software and services.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            1. Information We Collect
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>1.1 Account Information:</strong> When you create an account, we collect your email address, password, clinic name, first and last name (optional), and phone number (optional).
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>1.2 Patient and Clinical Data:</strong> As part of the Service, you may enter information about horses and their owners, including:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>Horse details (name, breed, date of birth, gender, color, microchip ID)</li>
              <li>Owner information (name, email, phone number)</li>
              <li>Medical records, SOAP notes, vaccination records, and treatment histories</li>
              <li>Appointment information and clinical documentation</li>
              <li>Discharge summaries and procedure records</li>
            </ul>
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>1.3 Billing Information:</strong> When you subscribe to a paid plan, payment information is collected and processed by Stripe, our third-party payment processor. We do not store your complete credit card information on our servers. We receive and store:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>Stripe customer ID</li>
              <li>Subscription status and tier information</li>
              <li>Billing email address</li>
              <li>Invoice and payment history</li>
            </ul>
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>1.4 Usage Data:</strong> We automatically collect certain information when you use our Service, including:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>Log data (IP address, browser type, pages visited, time spent)</li>
              <li>Device information (device type, operating system)</li>
              <li>Feature usage and interaction patterns</li>
              <li>Error logs and diagnostic data</li>
            </ul>
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>1.5 Feedback and Communications:</strong> When you contact us or submit feedback through the Service, we collect the content of your messages and any associated metadata.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            2. How We Use Your Information
          </Typography>
          
          <Typography variant="body1" paragraph>
            We use the information we collect to:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>Provide, maintain, and improve the Service</li>
              <li>Process your subscription payments and manage your account</li>
              <li>Send you important updates, security alerts, and support messages</li>
              <li>Respond to your comments, questions, and feedback</li>
              <li>Monitor and analyze usage trends to improve user experience</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
              <li>Send you promotional communications (which you can opt out of at any time)</li>
              <li>Improve AI Features: Use aggregated, de-identified, or anonymized data to enhance the accuracy, performance, and capabilities of our AI-assisted features, including voice transcription, clinical documentation tools, and conversational AI functionality</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            3. Data Storage and Security
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>3.1 Data Storage:</strong> Your data is stored securely in PostgreSQL databases hosted on cloud infrastructure. We implement industry-standard security measures to protect your information.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>3.2 Authentication:</strong> User authentication is managed through Firebase Authentication, which provides secure, industry-standard authentication services.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>3.3 Security Measures:</strong> We implement appropriate technical and organizational measures to protect your data, including:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>Encryption of data in transit using SSL/TLS</li>
              <li>Encryption of sensitive data at rest</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular backups of all data</li>
            </ul>
          </Typography>

          <Typography variant="body1" paragraph>
            <strong>3.4 Data Retention:</strong> We retain your data for as long as your account is active or as needed to provide the Service. After account termination, we retain your data for 30 days to allow for data export, after which it is permanently deleted.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            4. Third-Party Services
          </Typography>
          
          <Typography variant="body1" paragraph>
            We use the following third-party services that may collect and process your information:
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>4.1 Firebase (Google):</strong> We use Firebase Authentication for user authentication and account management. Firebase's privacy practices are governed by Google's Privacy Policy.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>4.2 Stripe:</strong> All payment processing is handled by Stripe. When you subscribe to a paid plan, Stripe collects and processes your payment information according to their Privacy Policy. We do not have access to your complete credit card information.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>4.3 AI Services:</strong> The Service incorporates artificial intelligence features to assist with clinical documentation, voice transcription, SOAP note generation, and interactive AI assistance. To provide and continuously improve these features, data processed through AI components may be used to enhance model performance and service quality. Where technically feasible, we use de-identified or aggregated data for these purposes. You may opt out of AI feature improvements by contacting support, though this may limit certain functionality.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            5. Data Sharing and Disclosure
          </Typography>
          
          <Typography variant="body1" paragraph>
            We do not sell, rent, or trade your personal information or patient data to third parties. We may share your information only in the following circumstances:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li><strong>With your consent:</strong> When you explicitly authorize us to share specific information</li>
              <li><strong>Service providers:</strong> With trusted third-party service providers who assist us in operating the Service (e.g., hosting, payment processing), under strict confidentiality agreements</li>
              <li><strong>Legal requirements:</strong> When required by law, legal process, or government request</li>
              <li><strong>Protection of rights:</strong> To protect the rights, property, or safety of StableTrack, our users, or the public</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, where the acquiring entity agrees to honor this Privacy Policy</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            6. Your Rights and Choices
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>6.1 Access and Portability:</strong> You have the right to access and export your data at any time through the Service's export features.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>6.2 Correction:</strong> You can update or correct your account information and customer data at any time through your account settings.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>6.3 Deletion:</strong> You may request deletion of your account and associated data. Upon deletion, your data will be permanently removed after 30 days.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>6.4 Marketing Communications:</strong> You can opt out of promotional emails by following the unsubscribe link in any marketing email or by contacting us.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>6.5 Cookies:</strong> You can control cookies through your browser settings. Note that disabling cookies may affect the functionality of the Service.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            7. Veterinary Medical Records
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>7.1 Data Ownership:</strong> You retain all ownership rights to the veterinary medical records and patient data you enter into StableTrack. We act solely as a data processor on your behalf.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>7.2 Professional Responsibility:</strong> You are responsible for ensuring that your use of the Service and handling of patient data complies with all applicable veterinary practice laws, regulations, and professional standards.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>7.3 Record Retention:</strong> While we maintain backups of your data, you are responsible for maintaining appropriate records in accordance with your professional and legal obligations.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            8. International Data Transfers
          </Typography>
          
          <Typography variant="body1" paragraph>
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using the Service, you consent to the transfer of your information to our facilities and to the third parties with whom we share it as described in this Privacy Policy.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            9. Children's Privacy
          </Typography>
          
          <Typography variant="body1" paragraph>
            StableTrack is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you become aware that a child has provided us with personal information, please contact us, and we will take steps to delete such information.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            10. Cookies and Tracking Technologies
          </Typography>
          
          <Typography variant="body1" paragraph>
            We use cookies and similar tracking technologies to:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>Maintain your session and keep you logged in</li>
              <li>Remember your preferences and settings</li>
              <li>Analyze how you use the Service to improve functionality</li>
              <li>Provide security features and detect fraudulent activity</li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            You can control cookies through your browser settings, but some features of the Service may not function properly if cookies are disabled.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            11. Changes to This Privacy Policy
          </Typography>
          
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top. We will also send you an email notification for significant changes. Your continued use of the Service after such changes constitutes your acceptance of the updated Privacy Policy.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            12. Data Protection Rights (GDPR)
          </Typography>
          
          <Typography variant="body1" paragraph>
            If you are located in the European Economic Area (EEA), you have certain data protection rights under the General Data Protection Regulation (GDPR), including:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>The right to access, update, or delete your personal information</li>
              <li>The right to rectification if your information is inaccurate or incomplete</li>
              <li>The right to object to processing of your personal information</li>
              <li>The right to request restriction of processing your personal information</li>
              <li>The right to data portability</li>
              <li>The right to withdraw consent at any time</li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            To exercise these rights, please contact us through the support channels in the application.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            13. California Privacy Rights (CCPA)
          </Typography>
          
          <Typography variant="body1" paragraph>
            If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA), including:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>The right to know what personal information we collect, use, and disclose</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to opt-out of the sale of personal information (note: we do not sell personal information)</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            14. Contact Us
          </Typography>
          
          <Typography variant="body1" paragraph>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us through:
          </Typography>
          <Typography variant="body1" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>The support channels within the StableTrack application</li>
              <li>The feedback widget available in the Service</li>
            </ul>
          </Typography>
          <Typography variant="body1" paragraph>
            We will respond to your inquiry within a reasonable timeframe.
          </Typography>

          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              By using StableTrack, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => navigate('/terms')}
                sx={{ mr: 2 }}
              >
                Terms of Service
              </MuiLink>
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => navigate('/signup')}
              >
                Back to Signup
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Privacy;
